/**
 * 限额调整页面
 */

import { useState, useCallback, useEffect } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import SkeletonBox from '@/components/SkeletonBox'
import CardView from '../components/card-view'
import { APIWeb } from '@/api'

// 限额项类型
interface LimitItem {
  key: 'daily' | 'weekly' | 'monthly' | 'transaction'
  apiKey: 'daily_max_amt' | 'weekly_max' | 'monthly_max' | 'max_per_tran'
  label: string
  value: string
  editable: boolean
  placeholder: string
}

// 可编辑限额配置类型
interface AllowedLimitChange {
  daily: boolean
  weekly: boolean
  monthly: boolean
  transaction: boolean
}

// 卡片信息接口
interface CardInfo {
  maskedPan?: string
  balance?: number
  backgroundImage?: string
  cardIssued?: boolean
  status?: string
}

export default function CardLimit() {
  const { t } = useTranslation()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const cardId = searchParams.get('id') || ''

  // 从路由 state 获取卡片信息
  const cardInfo: CardInfo = location.state || {}

  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)

  // 限额数据
  const [limits, setLimits] = useState<LimitItem[]>([
    {
      key: 'daily',
      apiKey: 'daily_max_amt',
      label: t('日限额'),
      value: '',
      editable: false,
      placeholder: t('不限额')
    },
    {
      key: 'weekly',
      apiKey: 'weekly_max',
      label: t('周限额'),
      value: '',
      editable: false,
      placeholder: t('不限额')
    },
    {
      key: 'monthly',
      apiKey: 'monthly_max',
      label: t('月限额'),
      value: '',
      editable: false,
      placeholder: t('不限额')
    },
    {
      key: 'transaction',
      apiKey: 'max_per_tran',
      label: t('单笔交易限额'),
      value: '',
      editable: false,
      placeholder: t('不限额')
    }
  ])

  // 获取卡片详情和限额配置
  useEffect(() => {
    const fetchCardDetail = async () => {
      if (!cardId) return

      try {
        setPageLoading(true)
        const res = await APIWeb.getVirtualCardDetailV2({
          item_id: Number(cardId),
          issued: true
        })

        const card = res?.card
        const balanceLimit = card?.balance_limit
        const allowedLimitChangeStr = card?.product?.allowed_limit_change

        // 解析可编辑字段配置
        let allowedLimitChange: AllowedLimitChange = {
          daily: false,
          weekly: false,
          monthly: false,
          transaction: false
        }

        if (allowedLimitChangeStr) {
          try {
            allowedLimitChange = JSON.parse(allowedLimitChangeStr)
          } catch (e) {
            console.error('解析 allowed_limit_change 失败:', e)
          }
        }

        // 格式化限额值（负数或0表示不限额，显示为空）
        const formatLimitValue = (value?: number) => {
          if (value === undefined || value === null || value <= 0) return ''
          return String(value)
        }

        // 更新限额数据
        setLimits(prev => prev.map(item => ({
          ...item,
          editable: allowedLimitChange[item.key] ?? false,
          value: formatLimitValue(balanceLimit?.[item.apiKey])
        })))
      } catch (error) {
        console.error('获取卡片详情失败:', error)
        Toast.fail(t('获取卡片信息失败'))
      } finally {
        setPageLoading(false)
      }
    }

    fetchCardDetail()
  }, [cardId, t])

  // 更新限额值
  const handleLimitChange = useCallback((key: string, value: string) => {
    // 只允许输入数字和小数点
    const numericValue = value.replace(/[^\d.]/g, '')
    // 限制只能有一个小数点
    const parts = numericValue.split('.')
    const formattedValue = parts.length > 2
      ? parts[0] + '.' + parts.slice(1).join('')
      : numericValue

    setLimits(prev => prev.map(item =>
      item.key === key ? { ...item, value: formattedValue } : item
    ))
  }, [])

  // 格式化显示值（添加千分位）
  const formatDisplayValue = (value: string) => {
    if (!value) return ''
    const num = parseFloat(value)
    if (isNaN(num)) return value
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // 确认变更
  const handleConfirm = useCallback(async () => {
    if (!cardId) {
      Toast.info(t('参数错误'))
      return
    }

    try {
      setLoading(true)

      // 构建请求参数，只包含可编辑的字段
      const params: {
        card_id: number
        daily_max_amt?: number
        weekly_max?: number
        monthly_max?: number
        max_per_tran?: number
      } = {
        card_id: Number(cardId)
      }

      // 遍历限额项，只添加可编辑的字段
      limits.forEach(item => {
        if (item.editable) {
          // 空值表示不限额，传 undefined 或不传
          // 有值则转换为数字
          const value = item.value ? Number(item.value) : undefined
          params[item.apiKey] = value
        }
      })

      await APIWeb.adjustCardLimitV2(params)
      Toast.success(t('限额调整成功'))
    } catch (error: any) {
      console.error('限额调整失败:', error)
      Toast.fail(error?.message || t('限额调整失败'))
    } finally {
      setLoading(false)
    }
  }, [cardId, limits, t])

  // 检查是否有可编辑的限额项（有可编辑项就允许提交，空值表示不限额）
  const hasEditableLimit = limits.some(item => item.editable)

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('调整限额')}</span>}
      />

      <div className="flex-1 px-5 pt-4 pb-32 overflow-auto">
        {/* 卡片预览 */}
        <CardView
          cardStatus={cardInfo.status}
          cardIssued={cardInfo.cardIssued !== false}
          maskedPan={cardInfo.maskedPan}
          balance={cardInfo.balance}
          backgroundImage={cardInfo.backgroundImage}
          className="w-full"
        />

        {/* 限额列表 */}
        <div className="mt-6 space-y-5">
          {pageLoading ? (
            // 加载骨架屏
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <SkeletonBox className="w-16 h-4 rounded mb-2" />
                  <SkeletonBox className="w-full h-14 rounded-xl" />
                </div>
              ))}
            </>
          ) : (
            limits.map((item) => (
              <div key={item.key}>
                {/* 标签 */}
                <div className="text-sm font-medium text-[#667085] mb-2">
                  {item.label}
                </div>

                {/* 输入框 */}
                <div className={`
                  flex items-center h-14 px-4 rounded-xl border-solid border-1 border-[#EAECF0]
                  ${item.editable ? 'bg-white' : 'bg-[#F9FAFB]'}
                `}>
                  <input
                    type="text"
                    maxLength={16}
                    inputMode="decimal"
                    value={item.editable ? item.value : formatDisplayValue(item.value)}
                    onChange={(e) => item.editable && handleLimitChange(item.key, e.target.value)}
                    placeholder={item.placeholder}
                    disabled={!item.editable}
                    className={`
                      flex-1 h-full bg-transparent outline-none text-base
                      ${item.editable ? 'text-[#1D2129]' : 'text-[#C9CDD4]'}
                      placeholder-[#C9CDD4]
                    `}
                  />
                  <span className={`
                    text-sm font-bold ml-2
                    ${item.editable ? 'text-[#1D2129]' : 'text-[#C9CDD4]'}
                  `}>
                    USD
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 底部按钮 */}
      <FixedBottom className="bg-white">
        {/* 提示信息 */}
        <div className="pb-2 text-center text-sm text-[#86909C]">
          {t('限额调整范围')}：$10-$100,000
        </div>
        <CustomButton
          onClick={handleConfirm}
          loading={loading}
          disabled={!hasEditableLimit}
        >
          {t('确认变更')}
        </CustomButton>
      </FixedBottom>
    </PageLayout>
  )
}

